import { updateEvent } from '@dispatch/shared/services';
import {
  Event,
  SuperApiTask,
  UpdateEventAssignmentAndTimeParams,
} from '@dispatch/shared/types';
import { useMutation, useQueryClient } from 'react-query';
import { z } from 'zod';

type OldResourceContext = {
  resourceEvents: Event[];
  removedEvent?: Event;
};
type NewResourceContext = {
  resourceEvents: Event[];
  updatedEvent: Event;
};

type UpdateContext = {
  oldResource: OldResourceContext;
  newResource?: NewResourceContext;
  eventUpdate: UpdateEventAssignmentAndTimeParams;
};

type ErrorResponse = {
  response: {
    data: {
      detail: string;
    };
    status: number;
  };
};

type Task = z.infer<typeof SuperApiTask>;

export const useUpdateEventResourceAndTime = (
  onSuccess: () => void,
  onError: (errorResponse: { status: number; message: string }) => void
) => {
  const queryClient = useQueryClient();

  const { mutate, error, status } = useMutation(
    'update-event',
    (data: UpdateEventAssignmentAndTimeParams) => {
      const task = data.event as Task;
      return updateEvent(
        data.newResourceId,
        data.startDateTime,
        data.endDateTime,
        data.reasonCode,
        task
      );
    },
    {
      onMutate: async (
        eventUpdate: UpdateEventAssignmentAndTimeParams
      ): Promise<UpdateContext> => {
        async function removeFromOldResource(
          update: UpdateEventAssignmentAndTimeParams
        ): Promise<OldResourceContext> {
          // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
          await queryClient.cancelQueries([
            'resource-events',
            eventUpdate.oldResourceId,
          ]);
          // Snapshot the previous value
          const resourceEvents = queryClient.getQueryData<Event[]>([
            'resource-events',
            eventUpdate.oldResourceId,
          ]);
          const removedEvent = resourceEvents?.find(
            (x) => x.externalId === update.eventId
          );
          const newResources = resourceEvents?.filter(
            (x) => x.externalId !== update.eventId
          );
          // Make optimistic update
          queryClient.setQueryData(
            ['resource-events', update.oldResourceId],
            newResources
          );
          return {
            resourceEvents: resourceEvents || [],
            removedEvent,
          };
        }
        async function updateNewResource(
          update: UpdateEventAssignmentAndTimeParams,
          removedEvent?: Event
        ): Promise<NewResourceContext | undefined> {
          if (!removedEvent) {
            return;
          }

          // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
          await queryClient.cancelQueries([
            'resource-events',
            eventUpdate.newResourceId,
          ]);
          // Snapshot the previous value
          const resourceEvents = queryClient.getQueryData<Event[]>([
            'resource-events',
            eventUpdate.newResourceId,
          ]);
          const updatedEvent: Event = {
            ...removedEvent,
            resourceId: update.newResourceId,
            startDateTime: new Date(update.startDateTime).toISOString(),
            endDateTime: new Date(update.endDateTime).toISOString(),
          };
          // Make optimistic update
          queryClient.setQueryData(
            ['resource-events', update.newResourceId],
            [...(resourceEvents || []), updatedEvent]
          );
          return {
            resourceEvents: resourceEvents || [],
            updatedEvent,
          };
        }

        const oldResource = await removeFromOldResource(eventUpdate);
        const newResource = await updateNewResource(
          eventUpdate,
          oldResource.removedEvent
        );

        // Return a context with the previous
        return { oldResource, newResource, eventUpdate };
      },
      onSuccess,
      // If the mutation fails, use the context we returned above
      onError: (_err: ErrorResponse, _params, context?: UpdateContext) => {
        queryClient.setQueryData(
          ['resource-events', context?.eventUpdate.oldResourceId],
          context?.oldResource.resourceEvents
        );
        queryClient.setQueryData(
          ['resource-events', context?.eventUpdate.newResourceId],
          context?.newResource?.resourceEvents
        );
        onError({
          status: _err.response.status,
          message: _err.response.data.detail,
        });
      },

      // Always refetch after error or success:
      onSettled: (
        _data,
        _error,
        update: UpdateEventAssignmentAndTimeParams
      ) => {
        queryClient.invalidateQueries([
          'resource-events',
          update.oldResourceId,
        ]);
        queryClient.invalidateQueries([
          'resource-events',
          update.newResourceId,
        ]);
        // should invalidate schedule data so that the data is updated to reflect the task change
        queryClient.invalidateQueries(['schedule-data']);
        queryClient.invalidateQueries(['schedule-data-infinite']);
      },
    }
  );

  return {
    updateEvent: mutate,
    error,
    status,
  };
};
